import React, { useState } from 'react';
import { Switch, showNotif } from '@p81-common/ui';
import { useTranslation } from 'react-i18next';
import { modalActionBody } from 'components/common/Elements/NewModal/newModal';
import { putRequest } from 'utils/requests';
import { Text, WithTooltip } from 'components/common/Elements/Elements';
import useStyles from './styles';

interface AutoAssignToggleProps {
  featureId: string;
  featureName: string;
  initialEnabled: boolean;
  onToggleSuccess: () => void;
  disabled?: boolean;
}

const AutoAssignToggle: React.FC<AutoAssignToggleProps> = ({
  featureId,
  featureName,
  initialEnabled,
  onToggleSuccess,
  disabled = false
}) => {
  const classes = useStyles();
  const { t } = useTranslation(['activity']);
  const [enabled, setEnabled] = useState(initialEnabled);
  const [loading, setLoading] = useState(false);

  const handleToggle = () => {
    const newState = !enabled;
    const confirmText = newState
      ? t('activity:featureFlag.AUTO_ASSIGN_ENABLE_CONFIRM', { featureName })
      : t('activity:featureFlag.AUTO_ASSIGN_DISABLE_CONFIRM', { featureName });

    modalActionBody({
      title: newState
        ? t('activity:featureFlag.AUTO_ASSIGN_ENABLE_TITLE')
        : t('activity:featureFlag.AUTO_ASSIGN_DISABLE_TITLE'),
      text: <Text className="">{confirmText}</Text>,
      callBack: () => performToggle(newState),
      okText: newState
        ? t('activity:featureFlag.AUTO_ASSIGN_ENABLE_BTN')
        : t('activity:featureFlag.AUTO_ASSIGN_DISABLE_BTN'),
      cancelText: t('activity:common.CANCEL')
    });
  };

  const performToggle = (newState: boolean) => {
    setLoading(true);
    putRequest(`/product/fflag/${featureId}/autoassign`, {
      enabled: newState
    })
      .then(() => {
        setEnabled(newState);
        onToggleSuccess();
        showNotif(
          newState
            ? t('activity:featureFlag.AUTO_ASSIGN_ENABLED_SUCCESS')
            : t('activity:featureFlag.AUTO_ASSIGN_DISABLED_SUCCESS'),
          { alertProps: { severity: 'success' } }
        );
      })
      .catch(() => {
        showNotif(t('activity:featureFlag.AUTO_ASSIGN_UPDATE_ERROR'), {
          alertProps: { severity: 'error' }
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <WithTooltip text={t('activity:featureFlag.AUTO_ASSIGN_SWITCH_TOOLTIP')}>
      {
        (
          <div className={classes.container}>
            <span
              className={`${classes.label}${
                disabled ? ` ${classes.disabled}` : ''
              }`}
            >
              {t('activity:featureFlag.AUTO_ASSIGN_SWITCH_LABEL')}
            </span>
            <Switch
              checked={enabled}
              onChange={handleToggle}
              disabled={loading || disabled}
              size="small"
            />
          </div>
        ) as any
      }
    </WithTooltip>
  );
};

export default AutoAssignToggle;
